import { useMemo, useState } from 'react';
import { Chip, SectionLabel, SimulatedTag } from '@hive/ui';
import { api, errorMessage, type Programme, type ProgrammeItem, type Screen } from '../api/client';
import { venueTime } from '../lib/format';
import { Dialog } from './Dialog';

type Filter = 'all' | 'football' | 'cricket' | 'esports' | 'channels' | 'sponsor';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'football', label: 'Football' },
  { id: 'cricket', label: 'Cricket' },
  { id: 'esports', label: 'Esports' },
  { id: 'channels', label: 'Channels' },
  { id: 'sponsor', label: 'Sponsor loops' },
];

interface Props {
  screen: Screen;
  screens: Screen[];
  programme: Programme | null;
  timezone?: string;
  onUpdated(screens: Screen[]): void;
  onClose(): void;
}

/** "Change what's playing on main bar": live now, upcoming today, channels, loops. */
export function Picker({ screen, screens, programme, timezone, onUpdated, onClose }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matches = (i: ProgrammeItem) => filter === 'all' || (filter === 'channels' ? i.kind === 'channel' : i.sport === filter);
  const live = useMemo(() => programme?.live.filter(matches) ?? [], [programme, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const upcoming = useMemo(() => programme?.upcoming.filter(matches) ?? [], [programme, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const channels = useMemo(() => programme?.channels.filter(matches) ?? [], [programme, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const loops = useMemo(() => programme?.loops.filter(matches) ?? [], [programme, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = screen.nowPlaying;
  const currentLabel = current ? (current.kind === 'loop' ? current.title : current.competition ?? current.title) : screen.channel?.name ?? 'the ambient screen';
  const others = screens.filter((s) => s.id !== screen.id && s.status !== 'unpaired');

  async function run(key: string, action: () => Promise<{ data?: Screen | Screen[]; error?: unknown }>) {
    setBusy(key);
    setError(null);
    try {
      const { data, error } = await action();
      if (!data) return setError(errorMessage(error));
      onUpdated(Array.isArray(data) ? data : [data]);
      onClose();
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setBusy(null);
    }
  }

  const put = (item: ProgrammeItem) =>
    run(item.fixtureId, () => api.PUT('/v1/screens/{screenId}/programme', { params: { path: { screenId: screen.id } }, body: { fixtureId: item.fixtureId } }));
  const putAll = (item: ProgrammeItem) =>
    run(`all:${item.fixtureId}`, () =>
      api.PUT('/v1/venues/{venueId}/programme', { params: { path: { venueId: screen.venueId } }, body: { fixtureId: item.fixtureId } }),
    );
  const tuneOut = () => run('off', () => api.DELETE('/v1/screens/{screenId}/channel', { params: { path: { screenId: screen.id } } }));

  const row = (item: ProgrammeItem) => {
    const here = current?.fixtureId === item.fixtureId;
    const elsewhere = (item.playingOn ?? []).filter((n) => n !== screen.name).map((n) => n.toLowerCase());
    const detail =
      item.status === 'live'
        ? [item.competition, item.clock, item.score].filter(Boolean).join(' · ')
        : item.kind === 'loop'
          ? item.detail ?? 'Sponsor loop'
          : item.kind === 'channel'
            ? item.detail ?? 'Always on'
          : [item.competition, item.detail].filter(Boolean).join(' · ');
    return (
      <li key={item.fixtureId} className={`k-pick${here ? ' k-pick--here' : ''}`}>
        <button type="button" className="k-pick__main" disabled={busy !== null || here} onClick={() => put(item)}>
          <span aria-hidden="true" className={`k-pick__tile k-pick__tile--${item.kind === 'channel' ? 'channel' : item.sport ?? 'sponsor'}`}>
            {item.competitionShort ?? (item.kind === 'loop' ? 'LOOP' : item.kind === 'channel' ? 'TV' : '')}
          </span>
          <span className="k-pick__text">
            <span className="k-pick__title">{item.title}</span>
            <span className="k-pick__detail">
              {detail}
              {item.status === 'live' && item.scoreSource === 'simulated' && (item.score || item.clock) && <SimulatedTag title="Score and clock are simulated" />}
              {elsewhere.length > 0 && ` · also on ${elsewhere.join(', ')}`}
            </span>
          </span>
          {here ? (
            <span className="hv-pill hv-pill--live">
              <span className="hv-dot hv-dot--live hv-dot--sm" />
              Playing here
            </span>
          ) : item.status === 'live' ? (
            <span className="hv-pill hv-pill--live">
              <span className="hv-dot hv-dot--live hv-dot--sm" />
              {busy === item.fixtureId ? 'Switching…' : 'Live'}
            </span>
          ) : item.status === 'upcoming' ? (
            <span className="k-pick__time">{busy === item.fixtureId ? 'Switching…' : venueTime(item.startsAt, timezone)}</span>
          ) : (
            <span className="k-pick__time">{busy === item.fixtureId ? 'Switching…' : item.kind === 'channel' ? 'On now' : 'Loop'}</span>
          )}
        </button>
        {others.length > 0 && (
          <button type="button" className="hv-btn hv-btn--link hv-btn--sm k-pick__all" disabled={busy !== null} onClick={() => putAll(item)}>
            {busy === `all:${item.fixtureId}` ? 'Tuning…' : 'All screens'}
          </button>
        )}
      </li>
    );
  };

  return (
    <Dialog
      titleId="picker-title"
      title={`Change what's playing on ${screen.name.toLowerCase()}`}
      subtitle={`Screen ${[screen.code, screen.sizeInches ? `${screen.sizeInches} in` : null].filter(Boolean).join(' · ')} · currently ${currentLabel}`}
      onClose={onClose}
      footer={
        <>
          <span className="hv-muted k-foot-note">Takes effect on the screen within about two seconds</span>
          <div className="k-foot-actions">
            <button type="button" className="hv-btn hv-btn--secondary" disabled={busy !== null || !screen.channel} onClick={tuneOut}>
              {busy === 'off' ? 'Tuning out…' : 'Tune out'}
            </button>
            <button type="button" className="hv-btn hv-btn--secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      }
    >
      <div className="k-chips" role="group" aria-label="Filter">
        {FILTERS.map((f) => (
          <Chip key={f.id} selected={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label}
          </Chip>
        ))}
      </div>
      {error && (
        <p className="k-inline-error" role="alert">
          {error}
        </p>
      )}
      {!programme ? (
        <p className="hv-muted">Loading what's on…</p>
      ) : (
        <div className="k-pick-groups">
          {live.length > 0 && (
            <section className="k-pick-group">
              <SectionLabel>Live now</SectionLabel>
              <ul className="k-pick-list">{live.map(row)}</ul>
            </section>
          )}
          {upcoming.length > 0 && (
            <section className="k-pick-group">
              <SectionLabel>Upcoming today</SectionLabel>
              <ul className="k-pick-list">{upcoming.map(row)}</ul>
            </section>
          )}
          {channels.length > 0 && (
            <section className="k-pick-group">
              <SectionLabel>Channels</SectionLabel>
              <ul className="k-pick-list">{channels.map(row)}</ul>
            </section>
          )}
          {loops.length > 0 && (
            <section className="k-pick-group">
              <SectionLabel>Sponsor and ambient loops</SectionLabel>
              <ul className="k-pick-list">{loops.map(row)}</ul>
            </section>
          )}
          {!live.length && !upcoming.length && !channels.length && !loops.length && <p className="hv-muted">Nothing in this category today.</p>}
        </div>
      )}
    </Dialog>
  );
}
